// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function InvoiceMakerForContractorsTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="Invoice Maker for Contractors"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of
        “Invoice Maker for Contractors” (the “App”). By downloading,
        installing, accessing, or using the App, you agree to be bound by these
        Terms. If you do not agree, you must uninstall the App and discontinue
        all use. Where required by platform rules or local law, additional terms
        may apply; in case of conflict with App Store or Google Play terms, the
        platform terms may prevail to the extent required.
      </p>

      <div className={s.title}>2) Description of the App</div>
      <p>
        The App is an offline-first tool that allows you to create, view, and
        manage invoices and estimates on your device. You can enter customer
        details, add line items (such as descriptions, quantities, and prices),
        calculate subtotals, taxes, discounts, and totals, and generate
        documents for preview and export as PDF. You may then save or share PDFs
        using system sharing options (for example, email or messaging apps). The
        App does not process payments, collect funds, or act as an accounting,
        tax, or legal service.
      </p>

      <div className={s.title}>3) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction and, where
        relevant, are authorized to create invoices or estimates on behalf of a
        business or client. Where parental consent is required by law, a parent
        or guardian must approve use. By using the App, you confirm that you
        meet applicable age and eligibility requirements or have obtained the
        necessary consent and authorizations.
      </p>

      <div className={s.title}>
        4) Local Storage, Offline Use, and Data Loss Risk
      </div>
      <p>
        The App is designed to work primarily offline and store invoices,
        estimates, settings, and related data locally on your device (for
        example, using a local database and file storage). There is no built-in
        cloud account or remote backup service operated by us for invoice
        content in the MVP version. As a result:
      </p>
      <ul>
        <li>
          if you delete the App, reset your device, or lose access to your
          device, your locally stored data (including invoices and estimates)
          may be permanently lost;
        </li>
        <li>
          we cannot recover deleted or lost invoices, estimates, or other local
          records for you;
        </li>
        <li>
          you are responsible for creating your own backups (for example,
          exporting PDFs or using system-level backups) if you wish to preserve
          records.
        </li>
      </ul>
      <p>
        You should regularly export and store important documents in secure,
        independent locations outside the App as part of your record-keeping
        practices.
      </p>

      <div className={s.title}>
        5) Invoice Content, Calculations, and Your Responsibility
      </div>
      <p>
        You are solely responsible for all data you enter into the App,
        including your business details, client information, line items, prices,
        taxes, discounts, and currency settings. The App performs calculations
        (such as subtotals, tax, and totals) based on your inputs, but does not
        verify that your entries are correct, complete, or compliant with local
        laws or contractual obligations.
      </p>
      <p>
        You are responsible for:
      </p>
      <ul>
        <li>
          ensuring that invoices and estimates you create are accurate and
          consistent with your actual services, rates, and agreements;
        </li>
        <li>
          configuring tax and discount settings according to applicable tax
          rules and business practices;
        </li>
        <li>
          reviewing each document before sending it to a client and correcting
          any errors or omissions;
        </li>
        <li>
          complying with any legal, tax, and record-keeping requirements
          relevant in your jurisdiction.
        </li>
      </ul>
      <p>
        The App does not guarantee that any invoice or estimate generated will
        be legally valid, enforceable, or sufficient for accounting, tax, or
        regulatory purposes.
      </p>

      <div className={s.title}>6) Free Tier, Limits, and Premium Features</div>
      <p>
        The App may offer both a free tier and a subscription-based premium
        tier. In the free version, functionality may be limited—for example, a
        cap on the number of saved invoices or estimates, restricted access to
        personalization (such as adding a logo), or other feature limitations.
        The premium tier may unlock unlimited or higher limits on saved
        documents, customization options, and additional tools as described in
        the App or on the paywall screen. We may adjust limits, features, and
        tier distinctions over time.
      </p>

      <div className={s.title}>7) Subscriptions, Trials, and Billing</div>
      <p>
        Premium features may be made available through auto-renewable
        subscriptions or other in-app purchases (“Subscriptions”), such as a
        weekly plan with a free trial period. Exact pricing, trial length, and
        benefits may vary by region and may change over time as shown within the
        App and in the app stores.
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are governed by the
        policies of the relevant app store.
      </p>

      <div className={s.title}>8) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to premium
        features such as unlimited invoices, logo upload, or advanced settings.
        An internet connection may be required to validate or refresh
        entitlements after long offline periods. If platform billing reverses,
        refunds, or flags a transaction as fraudulent, related entitlements may
        be limited, suspended, or revoked.
      </p>

      <div className={s.title}>9) Use of PDFs and Sharing</div>
      <p>
        The App may generate PDF versions of your invoices and estimates using
        templated layouts and export libraries. Once created, PDFs can be
        stored locally or shared through system-level options (for example,
        email, messaging, or file-sharing apps). After export or sharing,
        documents may be stored or processed by third-party apps or services
        under their own terms and privacy policies, which we do not control.
      </p>
      <p>
        You are responsible for:
      </p>
      <ul>
        <li>
          confirming that exported PDFs accurately reflect your intended
          invoice or estimate before sending them to clients;
        </li>
        <li>
          ensuring that recipients receive and can open the documents;
        </li>
        <li>
          keeping backup copies of important PDFs outside the App if needed for
          accounting or dispute resolution.
        </li>
      </ul>

      <div className={s.title}>10) No Accounting, Tax, or Legal Advice</div>
      <p>
        The App is not an accounting system, tax engine, or legal advice tool.
        It does not automatically calculate or file sales tax, VAT, income tax,
        or other obligations and does not replace professional bookkeeping or
        legal review. Any summaries, totals, or labels in the App are provided
        for convenience and require your independent verification.
      </p>
      <p>
        You should consult accountants, tax professionals, or legal counsel if
        you need guidance on invoicing practices, tax treatments, contract
        terms, or compliance with local business regulations.
      </p>

      <div className={s.title}>11) Acceptable Use and Prohibited Conduct</div>
      <p>
        You agree to use the App only for lawful business and personal purposes
        and not to:
      </p>
      <ul>
        <li>
          generate fraudulent, misleading, or fictitious invoices or estimates
          for transactions that did not occur;
        </li>
        <li>
          use the App to facilitate money laundering, tax evasion, or other
          illegal activities;
        </li>
        <li>
          infringe intellectual property rights, privacy rights, or other rights
          of any person;
        </li>
        <li>
          attempt to reverse engineer, decompile, or extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          circumvent technical protections, usage limits, or paywalls
          separating free and premium features;
        </li>
        <li>
          use automated scripts or tools to overload, scrape, or misuse the
          App or any associated services.
        </li>
      </ul>
      <p>
        If we reasonably believe your use violates these rules or applicable
        law, we may restrict, suspend, or terminate your access and, where
        required, cooperate with platform operators or authorities.
      </p>

      <div className={s.title}>12) Intellectual Property</div>
      <p>
        You retain ownership of your business details, invoice content,
        customer information, and other data you enter into the App, subject to
        rights of third parties. By using the App, you grant us and our service
        providers a limited, non-exclusive, worldwide, royalty-free license to
        process and store such data as needed to operate, maintain, and improve
        the App (for example, to show lists of invoices and generate PDFs).
      </p>
      <p>
        All software, user interface designs, templates, icons, logos, and other
        materials provided with the App (excluding your data and third-party
        content) are owned by the publisher or its licensors and are protected
        by intellectual property laws. Subject to these Terms and any
        Subscription limitations, you are granted a limited, non-exclusive,
        non-transferable, revocable license to use the App and its templates for
        creating invoices and estimates for your own business or professional
        purposes.
      </p>

      <div className={s.title}>13) Third-Party Services and Analytics</div>
      <p>
        The App may use third-party libraries and services to generate PDFs,
        manage subscriptions, collect analytics, and report crashes. Examples
        include PDF engines, Firebase Analytics, Crashlytics, and RevenueCat.
        Technical and usage data may be shared with these providers to operate,
        secure, and improve the App. These providers act under their own terms
        and privacy policies, and we require them by contract to handle data
        appropriately and only for permitted purposes.
      </p>
      <p>
        Analytics events may include actions such as invoice_created,
        invoice_exported, paywall_opened, trial_started, and purchase_success.
        Where required by law, we will obtain necessary permissions or provide
        options to manage analytics preferences.
      </p>

      <div className={s.title}>14) No Warranties</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and non-infringement.
        We do not warrant that:
      </p>
      <ul>
        <li>
          invoices, estimates, or calculations generated by the App will be
          error-free or suitable for every legal or accounting requirement;
        </li>
        <li>
          the App will function without interruption, delays, or defects on all
          devices or OS versions;
        </li>
        <li>
          any stored data will never be lost, corrupted, or unavailable.
        </li>
      </ul>

      <div className={s.title}>15) Limitation of Liability</div>
      <p>
        To the extent allowed by law, we are not liable for any indirect,
        incidental, consequential, or punitive damages, loss of profits, loss of
        data (including loss of invoices, estimates, or exported PDFs), or
        business interruption arising from or related to your use of or
        inability to use the App, or from errors or omissions in invoices or
        calculations. Our total aggregate liability to you for direct damages
        arising out of or relating to the App will be limited to the amount you
        paid for Subscriptions during the twelve (12) months prior to the
        claim, or the minimum amount permitted by applicable law if greater.
        Some jurisdictions do not allow certain limitations or exclusions, so
        parts of this section may not apply to you.
      </p>

      <div className={s.title}>16) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your invoices or estimates, your
        failure to comply with tax or legal obligations, your breach of these
        Terms, or your violation of any rights of a third party.
      </p>

      <div className={s.title}>17) Termination</div>
      <p>
        We may suspend or terminate your access to the App or to specific
        features at any time if we reasonably believe you have violated these
        Terms, misused the App, or where required by law, platform rules, or
        security concerns. You may stop using the App at any time by
        uninstalling it and, where applicable, cancelling your Subscription via
        platform settings. Sections that by their nature should survive
        termination (for example, intellectual property, acceptable use,
        limitation of liability, and indemnification) will continue to apply
        after your access ends.
      </p>

      <div className={s.title}>18) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        Invoice Maker for Contractors, which explains how information is
        collected, used, stored, and shared, including device information,
        invoice metadata, and analytics. In case of conflict between these
        Terms and the Privacy Policy regarding data processing, the Privacy
        Policy will guide how we handle personal data, while these Terms govern
        your use of the App and its features. You should review the Privacy
        Policy regularly to understand current practices.
      </p>

      <div className={s.title}>19) Changes to the App and to These Terms</div>
      <p>
        We may update the App, add or remove features (including changes to free
        and premium offerings), or modify these Terms from time to time. The
        “Last updated” date at the top of this document indicates when these
        Terms were last revised. Material changes may be highlighted in the App
        or via other reasonable means. By continuing to use the App after
        changes take effect, you accept the updated Terms. If you do not agree
        to the revised Terms, you must stop using the App and uninstall it.
      </p>

      <div className={s.title}>20) Contact Information</div>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing app-specific or
        purchase-related matters.
      </p>
    </TermOfUseLayout>
  );
}
